/**
 * Terms of Service Route
 *
 * Full Terms of Service (Story 0.7) with:
 * - EU Data Act compliance (max 2-month termination, 30-day data export)
 * - Romanian law jurisdiction
 * - Third-party integration disclaimers
 * - Subscription & billing terms
 * - GDPR-aligned data handling
 */

import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { Card, CardContent } from "@provenleap/ui";
import {
  OVERAGE_RATES,
  PLAN_DISPLAY_NAMES,
  PLAN_LIMITS,
  PLAN_PRICING,
} from "@provenleap/types";
import type { Plan } from "@provenleap/types";
import { useSEO } from "../hooks/useSEO";
import { SEO_CONFIG } from "../seo/seoConfig";

export const Route = createFileRoute("/terms")({
  component: TermsPage,
});

const PAID_PLANS: Exclude<Plan, "free">[] = ["starter", "pro", "agency"];

function formatUsd(amount: number): string {
  const cents = Math.round(amount * 100);
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

function TermsPage() {
  const router = useRouter();

  // Title/description/canonical/OG with cleanup (Story 30.2 useSEO).
  useSEO(SEO_CONFIG["/terms"]!);

  return (
    <div id="top" className="min-h-screen bg-white p-4 sm:p-8">
      <div className="max-w-[800px] mx-auto">
        <div className="flex items-center gap-4 mb-4">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) {
                router.history.back();
              } else {
                router.navigate({ to: "/login" });
              }
            }}
            className="text-sm text-muted-foreground hover:underline inline-block cursor-pointer"
          >
            &larr; Back
          </button>
          <span className="text-sm text-muted-foreground">|</span>
          <Link
            to="/privacy"
            className="text-sm text-muted-foreground hover:underline inline-block"
          >
            See also our Privacy Policy
          </Link>
        </div>

        <article>
          <Card>
            <CardContent className="pt-6">
              {/* Header */}
              <header className="mb-8">
                <h1 className="text-2xl font-semibold mb-2">
                  Terms of Service
                </h1>
                <p className="text-sm text-muted-foreground">
                  Version 1.1 | Effective Date: July 10, 2026 | Last Updated:
                  July 10, 2026
                </p>
              </header>

              {/* Table of Contents */}
              <nav aria-label="Table of contents" className="mb-8">
                <h2 className="text-lg font-medium mb-3">Table of Contents</h2>
                <ol className="list-decimal pl-5 space-y-1 text-sm">
                  <li>
                    <a href="#agreement" className="underline hover:text-foreground">
                      Agreement to Terms
                    </a>
                  </li>
                  <li>
                    <a href="#description" className="underline hover:text-foreground">
                      Description of Service
                    </a>
                  </li>
                  <li>
                    <a href="#accounts" className="underline hover:text-foreground">
                      User Accounts
                    </a>
                  </li>
                  <li>
                    <a href="#acceptable-use" className="underline hover:text-foreground">
                      Acceptable Use
                    </a>
                  </li>
                  <li>
                    <a href="#intellectual-property" className="underline hover:text-foreground">
                      Intellectual Property
                    </a>
                  </li>
                  <li>
                    <a href="#third-party" className="underline hover:text-foreground">
                      Third-Party Integrations
                    </a>
                  </li>
                  <li>
                    <a href="#billing" className="underline hover:text-foreground">
                      Subscription &amp; Billing
                    </a>
                  </li>
                  <li>
                    <a href="#availability" className="underline hover:text-foreground">
                      Service Availability &amp; Limitations
                    </a>
                  </li>
                  <li>
                    <a href="#liability" className="underline hover:text-foreground">
                      Limitation of Liability
                    </a>
                  </li>
                  <li>
                    <a href="#termination" className="underline hover:text-foreground">
                      Termination
                    </a>
                  </li>
                  <li>
                    <a href="#disputes" className="underline hover:text-foreground">
                      Dispute Resolution
                    </a>
                  </li>
                  <li>
                    <a href="#changes" className="underline hover:text-foreground">
                      Changes to Terms
                    </a>
                  </li>
                </ol>
              </nav>

              {/* Section 1: Agreement to Terms */}
              <section id="agreement" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  1. Agreement to Terms
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    By creating a ProvenLeap account, you agree to be bound by
                    these Terms of Service (&ldquo;Terms&rdquo;). If you do not
                    agree to these Terms, do not use the service.
                  </p>
                  <p>
                    <strong>Eligibility:</strong> You must be at least 16 years
                    of age to use ProvenLeap. By using the service, you
                    represent that you meet this age requirement and that you
                    have the authority to enter into these Terms on behalf of
                    yourself or the organization you represent.
                  </p>
                  <p>
                    <strong>Business use:</strong> ProvenLeap is designed for
                    business use by marketing agencies and professionals. By
                    using the service, you represent that you are using it for
                    professional or commercial purposes.
                  </p>
                  <p>
                    These Terms, together with our{" "}
                    <Link to="/privacy" className="underline hover:text-foreground">
                      Privacy Policy
                    </Link>
                    , form the complete agreement between you and ProvenLeap
                    regarding use of the service.
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 2: Description of Service */}
              <section id="description" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  2. Description of Service
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    ProvenLeap is a marketing analytics and reporting
                    software-as-a-service (SaaS) platform designed for marketing
                    agencies. The service enables users to:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      Connect to third-party analytics and advertising platforms
                      via OAuth integrations
                    </li>
                    <li>
                      Aggregate marketing data from multiple sources into
                      unified dashboards
                    </li>
                    <li>
                      Generate professional client reports with customizable
                      templates
                    </li>
                    <li>
                      Receive AI-powered insights and recommendations based on
                      analytics data
                    </li>
                    <li>
                      Schedule automated report delivery to agency clients
                    </li>
                    <li>
                      Manage multiple client accounts from a single dashboard
                    </li>
                  </ul>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 3: User Accounts */}
              <section id="accounts" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  3. User Accounts
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    <strong>Registration:</strong> To use ProvenLeap, you must
                    create an account by providing a valid email address and
                    password, or by authenticating through a supported OAuth
                    provider (e.g., Google).
                  </p>
                  <p>
                    <strong>Account security:</strong> You are responsible for
                    maintaining the confidentiality of your account credentials.
                    You must immediately notify us at{" "}
                    <a
                      href="mailto:support@provenleap.com"
                      className="underline hover:text-foreground"
                    >
                      support@provenleap.com
                    </a>{" "}
                    if you become aware of any unauthorized use of your account.
                  </p>
                  <p>
                    <strong>Account responsibility:</strong> You are responsible
                    for all activities that occur under your account. ProvenLeap
                    is not liable for any loss or damage arising from
                    unauthorized use of your credentials.
                  </p>
                  <p>
                    <strong>Accurate information:</strong> You agree to provide
                    accurate, current, and complete information during
                    registration and to update your information as needed.
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 4: Acceptable Use */}
              <section id="acceptable-use" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  4. Acceptable Use
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>You agree not to use ProvenLeap to:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      Scrape, crawl, or extract data from the platform through
                      automated means beyond normal API usage
                    </li>
                    <li>
                      Abuse, disrupt, or interfere with the service or its
                      infrastructure
                    </li>
                    <li>
                      Attempt unauthorized access to other users&apos; accounts
                      or data
                    </li>
                    <li>
                      Use the service for any illegal purpose or in violation of
                      applicable laws
                    </li>
                    <li>
                      Reverse-engineer, decompile, or attempt to extract the
                      source code of the service
                    </li>
                    <li>
                      Resell, sublicense, or redistribute the service without
                      written permission
                    </li>
                    <li>
                      Upload or transmit viruses, malware, or other harmful code
                    </li>
                    <li>
                      Violate the terms of service of any third-party platform
                      connected through ProvenLeap
                    </li>
                  </ul>
                  <p>
                    Violation of these terms may result in suspension or
                    termination of your account.
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 5: Intellectual Property */}
              <section id="intellectual-property" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  5. Intellectual Property
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    <strong>Your data:</strong> You retain full ownership of all
                    data you upload to or generate through ProvenLeap, including
                    your reports, templates, and configurations. We claim no
                    intellectual property rights over your content.
                  </p>
                  <p>
                    <strong>ProvenLeap platform:</strong> ProvenLeap retains all
                    intellectual property rights in the platform, including its
                    software, algorithms, user interface designs, report
                    templates, and documentation. These Terms do not grant you
                    any rights to our intellectual property except the limited
                    right to use the service as described herein.
                  </p>
                  <p>
                    <strong>AI-generated content:</strong> Insights and
                    recommendations generated by ProvenLeap&apos;s AI features
                    are provided as part of the service. You may use, modify,
                    and include AI-generated content in your reports.
                  </p>
                  <p>
                    <strong>Feedback:</strong> If you provide feedback or
                    suggestions about the service, we may use them without
                    obligation to you.
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 6: Third-Party Integrations */}
              <section id="third-party" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  6. Third-Party Integrations
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    ProvenLeap connects to third-party platforms through OAuth
                    integrations to access analytics and marketing data. By
                    connecting a third-party platform, you acknowledge and agree
                    that:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      <strong>Platform terms apply:</strong> Each third-party
                      platform (Google, Meta, LinkedIn, TikTok, etc.) has its
                      own terms of service and privacy policies that govern the
                      data sourced from their platforms.
                    </li>
                    <li>
                      <strong>Data accuracy:</strong> ProvenLeap is not
                      responsible for the accuracy, completeness, or
                      availability of data provided by third-party platforms.
                    </li>
                    <li>
                      <strong>Platform changes:</strong> Third-party platforms
                      may change their APIs, data access policies, or
                      availability at any time without notice to ProvenLeap. We
                      will make reasonable efforts to adapt to such changes but
                      cannot guarantee uninterrupted access to third-party data.
                    </li>
                    <li>
                      <strong>Authorization:</strong> You represent that you have
                      the necessary authorization to connect the third-party
                      accounts you link through ProvenLeap, including accounts
                      belonging to your agency clients.
                    </li>
                    <li>
                      <strong>Token management:</strong> OAuth tokens are stored
                      encrypted and are used solely to access data on your
                      behalf. You may revoke access at any time by disconnecting
                      the integration within ProvenLeap or by revoking access
                      from the third-party platform directly.
                    </li>
                  </ul>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 7: Subscription & Billing */}
              <section id="billing" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  7. Subscription &amp; Billing
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <h3 className="text-lg font-medium mt-4">Plans</h3>
                  <p>
                    ProvenLeap offers the following subscription plans. The
                    client counts below are included active-client counts:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      <strong>{PLAN_DISPLAY_NAMES.free}</strong> ($0/month)
                      &mdash; {PLAN_LIMITS.free.clientLimit} active client included;
                      additional clients are not available on the free plan.
                    </li>
                    {PAID_PLANS.map((plan) => (
                      <li key={plan} data-testid={`billing-terms-${plan}`}>
                        <strong>{PLAN_DISPLAY_NAMES[plan]}</strong> ({formatUsd(
                          PLAN_PRICING[plan].monthly,
                        )}/month or {formatUsd(PLAN_PRICING[plan].annual)}/year)
                        &mdash; {PLAN_LIMITS[plan].clientLimit} active clients
                        included; each additional active client is {formatUsd(
                          OVERAGE_RATES[plan].monthly,
                        )}/month or {formatUsd(OVERAGE_RATES[plan].annual)}/year.
                      </li>
                    ))}
                  </ul>
                  <p>
                    Only active, non-demo clients count toward included capacity
                    and additional-client billing. Archived and demo clients do
                    not count. Annual list prices equal ten months of the monthly
                    list price. All list prices are in U.S. dollars (USD) and
                    exclude sales tax, VAT, GST, or similar charges that may apply
                    based on your billing details and applicable law. The Stripe
                    checkout confirmation or invoice is authoritative for the
                    final amount, currency, tax, and line-item breakdown.
                  </p>
                  <p>
                    Plan features and pricing may change; we will notify you of
                    changes to your recurring price at least 30 days in advance,
                    unless a different notice period is required by law.
                  </p>

                  <h3 className="text-lg font-medium mt-4">
                    Additional-Client Charges
                  </h3>
                  <p>
                    When an active client is added above your plan&apos;s included
                    count, an authorized billing administrator is shown a
                    Stripe-calculated proration before confirming the addition.
                    On a monthly plan, the prorated adjustment for the remaining
                    billing period normally appears on the next invoice. On an
                    annual plan, the prorated adjustment is invoiced immediately.
                    Ongoing additional clients are then billed at the published
                    monthly or annual rate above.
                  </p>
                  <p>
                    Archiving or deleting a client reduces the quantity used for
                    future billing, but does not generate a mid-cycle proration,
                    refund, or account credit. Stripe&apos;s confirmation and invoice
                    are the authoritative record of the charge, including
                    proration and cent rounding.
                  </p>

                  <h3 className="text-lg font-medium mt-4">
                    Payment Processing
                  </h3>
                  <p>
                    All payments are processed by Stripe, a PCI-DSS compliant
                    payment processor. ProvenLeap does not directly store your
                    credit card information.
                  </p>

                  <h3 className="text-lg font-medium mt-4">
                    Renewals, Cancellation, and Refunds
                  </h3>
                  <p>
                    Paid subscriptions renew automatically for the selected
                    monthly or annual interval until canceled. Cancellation stops
                    future renewals; its effective date is displayed before you
                    confirm and will not exceed any maximum notice period required
                    by applicable law. Cancellation does not by itself reverse a
                    settled subscription, additional-client, or tax charge.
                  </p>
                  <p>
                    Except where applicable law or a written agreement requires
                    otherwise, paid fees and unused subscription time are
                    non-refundable, and we do not provide prorated refunds or
                    credits for client removals. We will investigate and correct
                    duplicate or erroneous charges reported to{" "}
                    <a
                      href="mailto:support@provenleap.com"
                      className="underline hover:text-foreground"
                    >
                      support@provenleap.com
                    </a>
                    .
                  </p>

                  <h3 className="text-lg font-medium mt-4">
                    Cancellation (EU Data Act Compliance)
                  </h3>
                  <p>
                    You may cancel your subscription at any time. In accordance
                    with the EU Data Act (effective September 2025):
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      <strong>Maximum notice period:</strong> 2 months. You will
                      not be required to provide more than 2 months&apos; notice
                      for cancellation.
                    </li>
                    <li>
                      <strong>Data export:</strong> You may request an export of
                      your data within 30 days of cancellation. We target
                      providing your export within 24 hours.
                    </li>
                    <li>
                      <strong>Data deletion:</strong> After data export is
                      completed (or the 30-day export period expires), your data
                      will be deleted in accordance with our{" "}
                      <Link to="/privacy" className="underline hover:text-foreground">
                        Privacy Policy
                      </Link>{" "}
                      retention schedule.
                    </li>
                    <li>
                      <strong>Switching support:</strong> Free from January 2027
                      (reduced charges may apply until then) to assist you in
                      transitioning to another service.
                    </li>
                  </ul>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 8: Service Availability & Limitations */}
              <section id="availability" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  8. Service Availability &amp; Limitations
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    <strong>Best-effort availability:</strong> We strive to
                    maintain high availability of ProvenLeap but do not guarantee
                    uninterrupted or error-free operation. The service is
                    provided on a &ldquo;best effort&rdquo; basis.
                  </p>
                  <p>
                    <strong>Scheduled maintenance:</strong> We may perform
                    scheduled maintenance that temporarily affects service
                    availability. When possible, we will provide advance notice
                    of planned maintenance windows.
                  </p>
                  <p>
                    <strong>Infrastructure:</strong> ProvenLeap runs on
                    Cloudflare&apos;s global infrastructure. Service availability
                    may be affected by Cloudflare platform status. We are not
                    liable for outages caused by our infrastructure providers.
                  </p>
                  <p>
                    <strong>Solo tier:</strong> No service level agreement (SLA)
                    is provided for Solo-tier (free) accounts. Paid plans may include
                    SLA commitments as specified in your subscription agreement.
                  </p>
                  <p>
                    <strong>Rate limits:</strong> API and data access rates may
                    be limited based on your subscription plan to ensure fair
                    usage across all customers.
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 9: Limitation of Liability */}
              <section id="liability" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  9. Limitation of Liability
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    To the maximum extent permitted by applicable law:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      ProvenLeap shall not be liable for any indirect,
                      incidental, special, consequential, or punitive damages,
                      including loss of profits, data, or business
                      opportunities, arising from your use of or inability to
                      use the service.
                    </li>
                    <li>
                      Our total aggregate liability for any claims arising from
                      these Terms or the service shall not exceed the total fees
                      you paid to ProvenLeap in the{" "}
                      <strong>12 months preceding the claim</strong>.
                    </li>
                  </ul>
                  <p>
                    <strong>EU consumer protections:</strong> Nothing in these
                    Terms excludes or limits our liability for: (a) death or
                    personal injury caused by our negligence; (b) fraud or
                    fraudulent misrepresentation; (c) gross negligence or willful
                    misconduct; or (d) any other liability that cannot be
                    excluded or limited under applicable EU or Romanian law.
                  </p>
                  <p>
                    <strong>AI disclaimer:</strong> AI-generated insights are
                    provided for informational purposes only and should not be
                    relied upon as the sole basis for business decisions. We make
                    no warranties regarding the accuracy or completeness of
                    AI-generated content.
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 10: Termination */}
              <section id="termination" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  10. Termination
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <h3 className="text-lg font-medium mt-4">
                    User-Initiated Termination
                  </h3>
                  <p>
                    You may terminate your account at any time through your
                    account settings. Upon termination:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      Your data export will be available for 30 days (we target
                      24-hour export delivery).
                    </li>
                    <li>
                      After the 30-day period, your data will be deleted per our
                      retention policy.
                    </li>
                    <li>
                      Any prepaid subscription fees for the remaining billing
                      period are non-refundable unless required by applicable
                      law.
                    </li>
                  </ul>

                  <h3 className="text-lg font-medium mt-4">
                    ProvenLeap-Initiated Termination
                  </h3>
                  <p>
                    We may suspend or terminate your account with{" "}
                    <strong>30 days&apos; notice</strong>, except in cases of:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      Violation of these Terms of Service (immediate suspension
                      permitted)
                    </li>
                    <li>
                      Illegal activity (immediate suspension and termination
                      permitted)
                    </li>
                    <li>Non-payment of subscription fees</li>
                  </ul>

                  <h3 className="text-lg font-medium mt-4">
                    Data Handling Post-Termination
                  </h3>
                  <p>
                    After termination, your personal data will be handled in
                    accordance with the retention periods specified in our{" "}
                    <Link to="/privacy" className="underline hover:text-foreground">
                      Privacy Policy
                    </Link>
                    . Payment records will be retained for 7 years as required by
                    Romanian accounting law.
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 11: Dispute Resolution */}
              <section id="disputes" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  11. Dispute Resolution
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    <strong>Governing law:</strong> These Terms are governed by
                    and construed in accordance with the laws of Romania, without
                    regard to conflict of law principles.
                  </p>
                  <p>
                    <strong>Jurisdiction:</strong> Any disputes arising from
                    these Terms or the service shall be submitted to the
                    exclusive jurisdiction of the courts of Bucharest, Romania.
                  </p>
                  <p>
                    <strong>EU consumer rights:</strong> If you are an EU
                    consumer, you retain the right to bring proceedings in the
                    courts of your country of residence. Nothing in these Terms
                    affects your statutory rights as a consumer under applicable
                    EU law.
                  </p>
                  <p>
                    <strong>Data protection complaints:</strong> For data
                    protection-related disputes, you may lodge a complaint with
                    ANSPDCP (Romanian Data Protection Authority) or your local
                    EU supervisory authority. See our{" "}
                    <Link to="/privacy" className="underline hover:text-foreground">
                      Privacy Policy
                    </Link>{" "}
                    for details.
                  </p>
                  <p>
                    <strong>EU Online Dispute Resolution:</strong> The European
                    Commission provides an Online Dispute Resolution platform at{" "}
                    <a
                      href="https://ec.europa.eu/consumers/odr"
                      className="underline hover:text-foreground"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      https://ec.europa.eu/consumers/odr
                    </a>
                    .
                  </p>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Section 12: Changes to Terms */}
              <section id="changes" className="mb-8">
                <h2 className="text-xl font-semibold mb-3">
                  12. Changes to Terms
                </h2>
                <div className="text-base leading-relaxed max-w-prose space-y-3">
                  <p>
                    We may modify these Terms from time to time. When we make
                    material changes:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      We will notify you by email at least{" "}
                      <strong>30 days</strong> before the changes take effect.
                    </li>
                    <li>
                      The updated Terms will include a new effective date and
                      version number.
                    </li>
                    <li>
                      Your continued use of ProvenLeap after the effective date
                      constitutes acceptance of the updated Terms.
                    </li>
                    <li>
                      If you do not agree with the updated Terms, you have the
                      right to terminate your account before the changes take
                      effect. Your data export will be available for 30 days
                      following termination.
                    </li>
                  </ul>
                </div>
                <a
                  href="#top"
                  className="text-xs text-muted-foreground hover:underline mt-3 inline-block"
                >
                  &uarr; Back to top
                </a>
              </section>

              {/* Contact footer */}
              <footer className="border-t border-border pt-6 mt-8">
                <p className="text-base leading-relaxed max-w-prose">
                  If you have any questions about these Terms of Service, please
                  contact us at{" "}
                  <a
                    href="mailto:support@provenleap.com"
                    className="underline hover:text-foreground"
                  >
                    support@provenleap.com
                  </a>
                  . For data protection inquiries, contact{" "}
                  <a
                    href="mailto:privacy@provenleap.com"
                    className="underline hover:text-foreground"
                  >
                    privacy@provenleap.com
                  </a>
                  .
                </p>
              </footer>
            </CardContent>
          </Card>
        </article>
      </div>
    </div>
  );
}
